import {Component, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {DatePipe} from '@angular/common';
import {Router} from '@angular/router';
import {AuthenticationService} from '../../../_auth/authentication.service';
import {AppRoutes} from '../../../enums/routes';

/**
 * The waiting screen for an employee whose employment has not started yet (#864).
 *
 * The session is valid but held back by the `employment_gate` claim: the server lets it reach the forms, contact
 * and two-factor routes and answers everything else with `E_EMPLOYMENT_NOT_STARTED`. This screen says when access
 * opens and offers what there is to do before then.
 *
 * ### How it is lifted
 *
 * Nothing on this screen lifts the gate; the start date does. `GateRefreshService` arms its alarm on
 * `employmentStartsAt`, asks `/auth/gates/refresh` a few seconds after it, and swaps in the unrestricted session.
 * This component listens to the session and leaves for the employee panel as soon as it is no longer pending, so
 * the person sitting in front of it on their first morning is let in without signing in again.
 *
 * ### Why only two buttons
 *
 * Forms and contact are offered when the session says there is something open there (`review`). A `restricted`
 * forms or contact gate never gets here: the guard sends those screens first (see `lockoutRouteFor`). Two-factor
 * setup is not offered, because the two-factor screen only serves a session the company requires it of, and
 * such a session is sent there before this one.
 */
@Component({
  selector: 'app-employment-pending',
  standalone: true,
  imports: [DatePipe],
  templateUrl: './employment-pending.component.html'
})
export class EmploymentPendingComponent {
  private readonly auth = inject(AuthenticationService);
  private readonly router = inject(Router);

  /** When the employment starts, or null when the session does not say. */
  protected readonly startsAt = signal<Date | null>(null);
  /** A forms task is open but not blocking (`forms_gate = review`). */
  protected readonly formsOpen = signal(false);
  /** A contact task is open but not blocking (`contact_gate = review`). */
  protected readonly contactOpen = signal(false);

  constructor() {
    // `user` is a BehaviorSubject, so this runs once for the current session and again for every swap —
    // including the one GateRefreshService makes when the start date passes.
    this.auth.user.pipe(takeUntilDestroyed()).subscribe(() => this.sync());
  }

  /** Opens the forms screen, which the server keeps open to this session. */
  openForms(): void {
    this.router.navigate(['/' + AppRoutes.FormsRequired]).then();
  }

  /** Opens the contact-linking screen, which the server keeps open to this session. */
  openContact(): void {
    this.router.navigate(['/' + AppRoutes.ContactRequired]).then();
  }

  /** Signs out. The plain, non-forced logout, for the reason given on the two-factor screen. */
  logout(): void {
    this.auth.logout();
  }

  private sync(): void {
    // Signed out (or signing out): nothing to show, and navigating here would race the logout's own redirect.
    if (!this.auth.userValue) return;

    if (!this.auth.isEmploymentPending) {
      this.router.navigate(['/' + AppRoutes.EmployeePanel]).then();
      return;
    }

    this.startsAt.set(this.auth.employmentStartsAt);
    this.formsOpen.set(this.auth.isFormsReviewDue === true);
    this.contactOpen.set(this.auth.isContactReviewDue === true);
  }
}
